/**
 * Pure helpers for the ordered list of API server base URLs used for automatic
 * failover. Kept side-effect-free so they are trivially unit-testable; the axios
 * client (`client.ts`) owns the stateful "which server is active" bookkeeping.
 */

/**
 * Build an ordered, de-duplicated list of base URLs from the primary URL and an
 * optional comma-separated list of fallbacks. Whitespace is trimmed and empty
 * entries (e.g. from a trailing comma) are dropped. Order is preserved: primary
 * first, then fallbacks in declaration order.
 */
export function parseServerUrls(
  primary: string | undefined,
  fallbacksCsv: string | undefined
): string[] {
  const raw = [primary ?? '', ...(fallbacksCsv ?? '').split(',')];
  const seen = new Set<string>();
  const urls: string[] = [];
  for (const entry of raw) {
    const url = entry.trim();
    if (url && !seen.has(url)) {
      seen.add(url);
      urls.push(url);
    }
  }
  return urls;
}

/**
 * Index of the next server to try, wrapping back to the first after the last.
 * Returns the current index unchanged when there is at most one server, and is
 * safe for an empty list.
 */
export function nextServerIndex(current: number, total: number): number {
  if (total <= 1) return current > 0 ? 0 : 0;
  return (current + 1) % total;
}
